import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { getDashboardBiAll } from '../../../services/api';
import './index.css';

const TYPE_LABELS = { 1: '传统电商', 2: '兴趣电商', 3: '自营小程序', 5: '自营分销' };
const TYPE_COLORS = { 1: '#5470c6', 2: '#fac858', 3: '#54b92f', 5: '#fa8c16' };

function formatMonth(m) {
  const p = String(m).split('-');
  return `${parseInt(p[1])}月`;
}

function sortMonths(months) {
  return [...months].sort((a, b) => a.localeCompare(b));
}

function useFetch(fn) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn().then(res => { if (!cancelled) setData(res.data); })
      .catch(e => { if (!cancelled) { setData(null); setError(e.message || '请求失败'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return { data, loading, error };
}

function ChartSkeleton({ error, children }) {
  if (error) {
    return (
      <div style={{ height: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#ff4d4f', fontSize: 13 }}>
        <span>加载失败</span>
        <span style={{ color: '#999', fontSize: 12, marginTop: 4 }}>{error}</span>
      </div>
    );
  }
  return children || (
    <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 14 }}>
      加载中...
    </div>
  );
}

export default function BIScreen() {
  const { data: biData, error: fetchError } = useFetch(getDashboardBiAll);
  const salesSummary = biData?.salesSummary;
  const channelSales = biData?.channelSales;
  const channelRatio = biData?.channelRatio;
  const taskCompletion = biData?.taskCompletion;
  const shopOrders = biData?.shopOrders;
  const monthlyTotal = biData?.monthlyTotal;
  const teamRank = biData?.teamRank;
  const orderCancelStats = biData?.orderCancelStats;
  const productTop5 = biData?.productTop5;
  const shopCancelTop5 = biData?.shopCancelTop5;

  const [animatedSales, setAnimatedSales] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const target = salesSummary?.totalSales || 0;
    if (target === 0) { setAnimatedSales(0); return; }
    const duration = 2000;
    const startTime = performance.now();
    let rafId;
    const step = (timestamp) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setAnimatedSales(+(progress * target).toFixed(2));
      if (progress < 1) rafId = requestAnimationFrame(step);
      else setAnimatedSales(target);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [salesSummary]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const updateTime = new Date(now);
  updateTime.setHours(2, 30, 0, 0);
  if (now < updateTime) updateTime.setDate(updateTime.getDate() - 1);
  const diffMs = now - updateTime;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  let updateStr;
  if (diffMins < 60) updateStr = `${diffMins}分钟前`;
  else if (diffHours < 24) updateStr = `${diffHours}小时前`;
  else updateStr = `${diffDays}天前`;

  const dateStr = `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日 星期${['日','一','二','三','四','五','六'][now.getDay()]} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;

  // R2C1: 自营/分销渠道销售额统计
  const buildChannelSalesOption = (raw) => {
    if (!raw || raw.length === 0) return null;
    const byMonth = {};
    raw.forEach(r => {
      const m = r['月份'];
      if (!byMonth[m]) byMonth[m] = { self: 0, dist: 0 };
      if (r['渠道类型'] === '分销') byMonth[m].dist += Number(r['销售金额']);
      else byMonth[m].self += Number(r['销售金额']);
    });
    const months = sortMonths(Object.keys(byMonth));
    const selfData = months.map(m => Math.round(byMonth[m].self / 10000 * 100) / 100);
    const distData = months.map(m => Math.round(byMonth[m].dist / 10000 * 100) / 100);
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: v => v + '万',
        textStyle: { fontSize: 10 },
      },
      legend: { data: ['自营', '分销'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'value', axisLabel: { formatter: '{value}万' } },
      yAxis: { type: 'category', data: months.slice().reverse().map(formatMonth) },
      series: [
        { name: '自营', type: 'bar', data: selfData.slice().reverse(), itemStyle: { color: '#1890ff' }, label: { show: true, position: 'right', fontSize: 10 } },
        { name: '分销', type: 'bar', data: distData.slice().reverse(), itemStyle: { color: '#fa8c16' }, label: { show: true, position: 'right', fontSize: 10 } },
      ],
    };
  };

  // R2C2: 细分渠道销售占比
  const buildChannelRatioOption = (raw) => {
    if (!raw || raw.length === 0) return null;
    const typeNames = Object.values(TYPE_LABELS);
    const months = sortMonths([...new Set(raw.map(r => r['月份']))]);

    const byMonthType = {};
    raw.forEach(r => {
      const m = r['月份'];
      const t = r['类型'];
      if (!byMonthType[m]) byMonthType[m] = {};
      byMonthType[m][t] = (byMonthType[m][t] || 0) + Number(r['销售金额']);
    });

    const typeKeys = Object.keys(TYPE_LABELS).map(Number);
    const seriesData = {};
    months.forEach(m => {
      const total = typeKeys.reduce((s, t) => s + (byMonthType[m][t] || 0), 0);
      typeKeys.forEach(t => {
        if (!seriesData[t]) seriesData[t] = [];
        seriesData[t].push(total > 0 ? Math.round(((byMonthType[m][t] || 0) / total) * 10000) / 100 : 0);
      });
    });

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, textStyle: { fontSize: 10 } },
      legend: { data: typeNames },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: months.map(formatMonth) },
      yAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
      series: typeKeys.map(t => ({
        name: TYPE_LABELS[t],
        type: 'bar', stack: 'total',
        data: seriesData[t],
        itemStyle: { color: TYPE_COLORS[t] },
      })),
    };
  };

  // R2C3: 双环图
  const buildRingOption = (label, completed, total) => {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return {
      tooltip: { trigger: 'item', formatter: `{b}: ${pct}%`, textStyle: { fontSize: 10 } },
      series: [{
        type: 'pie', radius: ['60%', '80%'],
        label: {
          show: true, position: 'center',
          formatter: `{a|${pct}%}\n{b|${label}}`,
          rich: { a: { fontSize: 20, fontWeight: 'bold', color: '#333' }, b: { fontSize: 12, color: '#999' } },
        },
        data: [
          { value: pct, name: '累计完成', itemStyle: { color: '#54b92f' } },
          { value: Math.max(100 - pct, 0), name: '剩余目标', itemStyle: { color: '#eee' } },
        ],
      }],
    };
  };

  // R2C4: 各店铺月订单量
  const buildShopOrdersOption = (raw) => {
    if (!raw || raw.length === 0) return null;
    const shopSales = {};
    raw.forEach(r => {
      const name = r['店铺名称'];
      if (!shopSales[name]) shopSales[name] = { byMonth: {} };
      shopSales[name].byMonth[r['月份']] = (shopSales[name].byMonth[r['月份']] || 0) + Number(r['订单数量']);
    });
    const topShops = Object.keys(shopSales);
    const months = sortMonths([...new Set(raw.map(r => r['月份']))]);
    const colors = ['#5470c6', '#fac858', '#54b92f', '#ee6666', '#fa8c16'];
    return {
      tooltip: { trigger: 'axis', appendToBody: true, textStyle: { fontSize: 10 } },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: months.map(formatMonth) },
      yAxis: { type: 'value' },
      series: topShops.map((shop, i) => ({
        name: shop,
        type: 'line',
        data: months.map(m => shopSales[shop].byMonth[m] || 0),
        itemStyle: { color: colors[i % colors.length] },
      })),
    };
  };

  // R3C1: 全渠道月销售额
  const buildMonthlyTotalOption = (raw) => {
    if (!raw || raw.length === 0) return null;
    const months = sortMonths(raw.map(r => r['月份']));
    const data = months.map(m => {
      const row = raw.find(r => r['月份'] === m);
      return row ? Math.round(Number(row['销售金额'])/10000 * 100) / 100 : 0;
    });
    return {
      tooltip: { trigger: 'axis', formatter: params => `${params[0].name}<br/>销售额: ${params[0].value} 万`, textStyle: { fontSize: 10 }},
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: months.map(formatMonth) },
      yAxis: { type: 'value' },
      series: [{ type: 'bar', data, itemStyle: { color: '#54b92f' }, label: { show: true, position: 'top', color: '#333', fontWeight: 'bold', formatter: p => `${p.value.toFixed(2)}万` } }],
    };
  };

  // R3C2: 各销售团队累计销售额
  const buildTeamRankOption = (raw) => {
    if (!raw || raw.length === 0) return null;
    const colors = ['#ff7f50', '#4dc3a1', '#25b2ff', '#fac858', '#317535', '#59bcaa', '#ee6666', '#8a2be2', '#ff69b4', '#20b2aa'];
    return {
      tooltip: { trigger: 'item', formatter: params => `${params.name}: ${params.value}万`, textStyle: { fontSize: 10 } },
      series: [{
        type: 'treemap', width: '100%', height: '100%',
        breadcrumb: { show: false }, roam: false,
        itemStyle: { borderColor: '#fff' },
        data: raw.map((r, i) => ({
          value: Math.round(Number(r.total_sales) / 10000 * 100) / 100,
          name: r.team_name,
          itemStyle: { color: colors[i % colors.length] },
        })),
      }],
    };
  };

  // R3C4: 总订单/退单量
  const buildOrderCancelOption = (stats) => {
    if (!stats || !stats.orders || stats.orders.length === 0) return null;
    const months = sortMonths(stats.orders.map(r => r['月份']));
    const orderMap = {};
    stats.orders.forEach(r => { orderMap[r['月份']] = r; });
    const cancelMap = {};
    (stats.cancels || []).forEach(r => { cancelMap[r['月份']] = r; });
    const totalOrders = months.map(m => Number(orderMap[m]?.['总订单量'] || 0));
    const cancelOrders = months.map(m => Number(cancelMap[m]?.['退单量'] || 0));
    const cancelRate = months.map((m, i) => totalOrders[i] > 0 ? Math.round((cancelOrders[i] / totalOrders[i]) * 10000) / 100 : 0);
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, textStyle: { fontSize: 10 } },
      legend: { data: ['总订单量', '退单量', '退单率'] },
      grid: { left: '8%', right: '8%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: months.map(formatMonth) },
      yAxis: [
        { type: 'value', name: '订单量' },
        { type: 'value', name: '退单率', min: 0, max: 'auto', axisLabel: { formatter: '{value}%' } },
      ],
      series: [
        {
          name: '总订单量', type: 'bar', barWidth: '20%', data: totalOrders,
          label: { show: false, position: 'top', color: '#333', fontWeight: 'bold' },
        },
        {
          name: '退单量', type: 'bar', barWidth: '20%', data: cancelOrders,
          label: { show: false, position: 'top', color: '#d4380d' },
        },
        {
          name: '退单率', type: 'line', yAxisIndex: 1, smooth: true, data: cancelRate,
          label: { show: true, formatter: '{c}%', color: '#333' },
          itemStyle: { color: '#ff7f50' },
        },
      ],
    };
  };

  // R4C1: 全渠道产品销量 TOP5
  const buildProductTop5Option = (raw) => {
    if (!raw || raw.length === 0) return null;
    raw = raw.filter(r => Number(r['数量']) > 0);
    if (raw.length === 0) return null;
    const productNames = [...new Set(raw.map(r => r['material_name']))];
    const months = sortMonths([...new Set(raw.map(r => r['月份']))]);
    const byProdMonth = {};
    raw.forEach(r => {
      const key = `${r['material_name']}||${r['月份']}`;
      byProdMonth[key] = Number(r['数量']);
    });
    const activeProductNames = productNames.filter(name =>
      months.some(m => (byProdMonth[`${name}||${m}`] || 0) > 0)
    );
    const colors = ['#5470c6', '#fac858', '#54b92f', '#ee6666', '#fa8c16', '#8a2be2'];
    return {
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' }, appendToBody: true,
        textStyle: { fontSize: 10 },
        formatter: params => {
          const active = params.filter(p => p.value > 0);
          if (active.length === 0) return '';
          let html = active[0].axisValueLabel || active[0].name;
          active.forEach(p => { html += `<br/>${p.marker} ${p.seriesName}: ${p.value}`; });
          return html;
        },
      },
      legend: { data: activeProductNames, textStyle: { fontSize: 10 }, bottom: 0 },
      grid: {top:'2%', left: '3%', right: '4%', bottom: '30%', containLabel: true },
      xAxis: { type: 'category', data: months.map(formatMonth) },
      yAxis: { type: 'value' },
      series: activeProductNames.map((name, i) => ({
        name,
        type: 'bar',
        data: months.map(m => byProdMonth[`${name}||${m}`] || 0),
        itemStyle: { color: colors[i % colors.length] },
      })),
    };
  };

  // R4C2: 月店铺退单量 TOP5
  const buildShopCancelTop5Option = (raw) => {
    if (!raw || raw.length === 0) return null;
    raw = raw.map(r => ({
      ...r,
      shop_name: r['shop_name'] || (r['shop_id'] === 20506400 ? '维淳旗舰店（天猫）' : '未知店铺'),
    }));
    const shopNames = [...new Set(raw.map(r => r['shop_name']))];
    const months = sortMonths([...new Set(raw.map(r => r['月份']))]);
    const byShopMonth = {};
    raw.forEach(r => {
      const key = `${r['shop_name']}||${r['月份']}`;
      byShopMonth[key] = Number(r['退单量']);
    });
    const activeShopNames = shopNames.filter(name =>
      months.some(m => (byShopMonth[`${name}||${m}`] || 0) > 0)
    );
    const colors = ['#5470c6', '#fac858', '#54b92f', '#ee6666', '#fa8c16'];
    return {
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' }, appendToBody: true,
        textStyle: { fontSize: 10 },
        formatter: params => {
          const active = params.filter(p => p.value > 0);
          if (active.length === 0) return '';
          let html = active[0].axisValueLabel || active[0].name;
          active.forEach(p => { html += `<br/>${p.marker} ${p.seriesName}: ${p.value}`; });
          return html;
        },
      },
      legend: { data: activeShopNames, textStyle: { fontSize: 10 }, bottom: 0 },
      grid: {top:'2%', left: '3%', right: '4%', bottom: '36%', containLabel: true },
      xAxis: { type: 'category', data: months.map(formatMonth) },
      yAxis: { type: 'value' },
      series: activeShopNames.map((name, i) => ({
        name,
        type: 'bar',
        data: months.map(m => byShopMonth[`${name}||${m}`] || 0),
        itemStyle: { color: colors[i % colors.length] },
      })),
    };
  };

  const channelSalesOpt = buildChannelSalesOption(channelSales);
  const channelRatioOpt = buildChannelRatioOption(channelRatio);
  const shopOrdersOpt = buildShopOrdersOption(shopOrders);
  const monthlyTotalOpt = buildMonthlyTotalOption(monthlyTotal);
  const teamRankOpt = buildTeamRankOption(teamRank);
  const orderCancelOpt = buildOrderCancelOption(orderCancelStats);
  const productTop5Opt = buildProductTop5Option(productTop5);
  const shopCancelOpt = buildShopCancelTop5Option(shopCancelTop5);

  return (
    <div className="dashboard-grid">
      {/* R1: Header */}
      <div id="header-row">
        <div className="main-title">恒力源营销数字驾驶舱</div>
        <div className="kpi-row">
          今年线上累计销售金额：<span className="target-number">{animatedSales.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span> (元)
        </div>
        <div className="header-date">{dateStr} （数据更新于{updateStr}）</div>
      </div>

      {/* R2C1 */}
      <div id="chart-r2c1" className="card">
        <div className="card-title">自营 / 分销 渠道销售额统计(单位: 万)</div>
        {channelSalesOpt ? <ReactECharts option={channelSalesOpt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R2C2 */}
      <div id="chart-r2c2" className="card">
        <div className="card-title">细分渠道销售占比（按月）</div>
        {channelRatioOpt ? <ReactECharts option={channelRatioOpt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R2C3 */}
      <div id="chart-r2c3" className="card">
        <div className="card-title">全年任务与当月任务完成情况汇总</div>
        {taskCompletion ? (
          <div className="center-rings">
            <div className="ring-box">
              <ReactECharts option={buildRingOption('全年完成', taskCompletion.yearlyCompleted, taskCompletion.yearlyTask)} style={{ height: '100%', width: '100%' }} />
            </div>
            <div className="ring-box">
              <ReactECharts option={buildRingOption('当月完成', taskCompletion.monthlyCompleted, taskCompletion.monthlyTask)} style={{ height: '100%', width: '100%' }} />
            </div>
          </div>
        ) : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R2C4 */}
      <div id="chart-r2c4" className="card">
        <div className="card-title">各店铺月订单量 Top 10</div>
        {shopOrdersOpt ? <ReactECharts option={shopOrdersOpt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R3C1 */}
      <div id="chart-r3c1" className="card">
        <div className="card-title">平台销售额统计(单位: 万)</div>
        {monthlyTotalOpt ? <ReactECharts option={monthlyTotalOpt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R3C2-3 */}
      <div id="chart-r3c2-3" className="card">
        <div className="card-title">各销售团队累计销售额（按回款）</div>
        {teamRankOpt ? <ReactECharts option={teamRankOpt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R3C4 */}
      <div id="chart-r3c4" className="card">
        <div className="card-title">总订单 & 退单量及退单率统计 </div>
        {orderCancelOpt ? <ReactECharts option={orderCancelOpt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R4C1-2 */}
      <div id="chart-r4c1-2" className="card">
        <div className="card-title">全平台渠道产品动销 Top5</div>
        {productTop5Opt ? <ReactECharts option={productTop5Opt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>

      {/* R4C3-4 */}
      <div id="chart-r4c3-4" className="card">
        <div className="card-title">店铺退单量 Top5</div>
        {shopCancelOpt ? <ReactECharts option={shopCancelOpt} style={{ height: 250 }} className="card-chart" /> : <ChartSkeleton error={fetchError} />}
      </div>
    </div>
  );
}
